/**
 * Age gate — Alpine.data("provinceChanger")
 * Flow: GPS → Select Province Location → Rise Rewards → Nearby Stores.
 * Load after Persist/Focus plugins and before Alpine core (all deferred).
 */


// Smaller / more specific provinces first so overlapping bounding boxes resolve correctly.
const CANADIAN_PROVINCE_BOUNDS = [
  { name: 'Prince Edward Island', minLat: 45.9, maxLat: 47.1, minLng: -64.5, maxLng: -61.9 },
  { name: 'Nova Scotia', minLat: 43.4, maxLat: 47.1, minLng: -66.4, maxLng: -59.7 },
  { name: 'New Brunswick', minLat: 44.6, maxLat: 48.1, minLng: -69.05, maxLng: -63.7 },
  {
    name: 'Newfoundland and Labrador',
    minLat: 46.6,
    maxLat: 60.4,
    minLng: -67.8,
    maxLng: -52.6,
  },
  { name: 'Alberta', minLat: 49, maxLat: 60, minLng: -120, maxLng: -110 },
  { name: 'Saskatchewan', minLat: 49, maxLat: 60, minLng: -110, maxLng: -101.3 },
  { name: 'Manitoba', minLat: 49, maxLat: 60, minLng: -102, maxLng: -89 },
  { name: 'British Columbia', minLat: 48.3, maxLat: 60, minLng: -139.06, maxLng: -114 },
  { name: 'Quebec', minLat: 44.99, maxLat: 62.6, minLng: -79.8, maxLng: -57 },
  { name: 'Ontario', minLat: 41.7, maxLat: 56.9, minLng: -95.2, maxLng: -74.3 },
];

const PROVINCE_ALIASES = {
  AB: 'Alberta',
  Alberta: 'Alberta',
  BC: 'British Columbia',
  'British Columbia': 'British Columbia',
  MB: 'Manitoba',
  Manitoba: 'Manitoba',
  NB: 'New Brunswick',
  'New Brunswick': 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  'Newfoundland and Labrador': 'Newfoundland and Labrador',
  'Newfoundland and Labrador Province': 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  'Nova Scotia': 'Nova Scotia',
  ON: 'Ontario',
  Ontario: 'Ontario',
  PE: 'Prince Edward Island',
  PEI: 'Prince Edward Island',
  'Prince Edward Island': 'Prince Edward Island',
  QC: 'Quebec',
  Quebec: 'Quebec',
  Québec: 'Quebec',
  SK: 'Saskatchewan',
  Saskatchewan: 'Saskatchewan',
};

// Cart / Functions / pricing JSON expect two-letter codes (ON), not full names.
const PROVINCE_NAME_TO_CODE = {
  Alberta: 'AB',
  'British Columbia': 'BC',
  Manitoba: 'MB',
  'New Brunswick': 'NB',
  'Newfoundland and Labrador': 'NL',
  'Nova Scotia': 'NS',
  Ontario: 'ON',
  'Prince Edward Island': 'PE',
  Quebec: 'QC',
  Québec: 'QC',
  Saskatchewan: 'SK',
};

/**
 * Store-list diagnostics, off unless asked for.
 *
 * Turn on with ?debug_stores=1 on any URL, or
 * localStorage.setItem('vb_debug_stores', '1') to keep it across navigations.
 *
 * Exists because every way this list goes wrong looks the same from the modal - an
 * empty picker. A store missing from the payload, a store present but filed under the
 * wrong province, and a province matching nothing are indistinguishable without this.
 */
function storeDebugEnabled() {
  try {
    if (window.location.search.indexOf('debug_stores') !== -1) return true;
    return window.localStorage.getItem('vb_debug_stores') === '1';
  } catch (error) {
    return false;
  }
}

function storeDebug(label, payload) {
  if (!storeDebugEnabled()) return;
  console.log('%c[stores] ' + label, 'color:#0a7;font-weight:700', payload);
}

/** Province tally, plus the two disagreements that silently drop a store. */
function storeDebugBreakdown(stores) {
  if (!storeDebugEnabled()) return;

  const tally = {};
  const mismatched = [];
  const noProvince = [];

  (stores || []).forEach((store) => {
    const name = store.province == null ? '' : String(store.province).trim();
    const code = store.provinceCode == null ? '' : String(store.provinceCode).trim();
    const key = (name || '(blank)') + '  /  ' + (code || '(blank)');
    tally[key] = (tally[key] || 0) + 1;

    if (!name && !code) {
      noProvince.push({ id: store.id, name: store.name });
      return;
    }

    // filteredStores reads `province` FIRST and only falls back to the code, so when
    // the two resolve to different provinces the store is filed under the name's.
    const viaName = name ? PROVINCE_ALIASES[name] : null;
    const viaCode = code ? PROVINCE_ALIASES[code] : null;
    if (viaName && viaCode && viaName !== viaCode) {
      mismatched.push({
        id: store.id,
        store: store.name,
        province: name,
        provinceCode: code,
        filtersInto: viaName,
        shouldBe: viaCode,
      });
    }
  });

  console.log('%c[stores] tally by province / provinceCode', 'color:#0a7;font-weight:700');
  console.table(tally);

  if (mismatched.length) {
    console.warn('[stores] province disagrees with provinceCode - these filter into the WRONG province:');
    console.table(mismatched);
  }
  if (noProvince.length) {
    console.warn('[stores] ' + noProvince.length + ' store(s) have neither province nor provinceCode, so they match nothing:');
    console.table(noProvince);
  }
}

function provinceChanger({
  isDesignMode,
  enabledInDesignMode,
  forceShowOnRefresh,
  expirationDuration,
  currentProvince,
  currentLocationId,
  ageLimits,
  ageQuestionTemplate,
  stores,
  hasLocationProduct,
  locationProductUrl,
  locationVariantId,
  storeImageFallback,
  rewardsEnabled,
  rewardsPrimaryUrl,
  locationAllowUnselected,
  locationAutoDetect,
  cartItemCount,
}) {
  return {
    modalOpen: false,
    step: 'gps', // 'gps' | 'province' | 'rewards' | 'stores' | 'confirm-store' | 'location-warning'
    locating: false,
    locationError: '',
    storesLoading: false,
    changingStore: false,
    storeSearchQuery: '',
    ageAnswer: null, // 'yes' | 'no' | null
    ageConfirmed: false,
    ageError: false,
    userCoords: null,
    suggestedLocationId: null,
    pendingStoreId: null,
    // Where to land once the switch finishes, for callers that pick a store on the way
    // somewhere — a store page's "Shop this store". Null means stay on this page.
    pendingReturnTo: null,
    // The store being switched away from, fixed when the confirm step opens. selectStore
    // makes the new store current before the page reloads, so reading currentStore there
    // would name the new store on both sides while the switch is running.
    switchFromStoreId: null,
    pendingUnavailableTitles: [],
    skipLocationWarning: false,
    cartItemCount: Number(cartItemCount) || 0,
    cookieName: '_age_verified',
    cookieValue: true,
    ageVerified: false,
    ageLimits: ageLimits || {},
    ageQuestionTemplate: ageQuestionTemplate || 'Are you at least __AGE__+?',
    stores: Array.isArray(stores) ? stores : [],
    hasLocationProduct: Boolean(hasLocationProduct),
    locationProductUrl: locationProductUrl || null,
    locationVariantId: locationVariantId || null,
    storeImageFallback: storeImageFallback || null,
    locationAllowUnselected: Boolean(locationAllowUnselected),
    locationAutoDetect: Boolean(locationAutoDetect),
    storesFetched: false,
    rewardsEnabled: Boolean(rewardsEnabled),
    rewardsPrimaryUrl: rewardsPrimaryUrl || '',
    savedCurrentLocationId: currentLocationId ? String(currentLocationId) : null,
    selectedProvince: this.$persist(null).as('_selected_province'),
    provinceSelectOpen: false,
    selectedLocationId: this.$persist(null).as('_selected_location_id'),
    gpsPermissionChoice: this.$persist(null).as('_gps_permission_choice'),
    rewardsSeen: this.$persist(false).as('_rewards_gate_seen'),

    init() {
      this.stores = this.stores.map((store) => this.withStoreImage(store));

      this.$nextTick(() => {
        if (!this.selectedProvince) {
          this.selectedProvince = null;
        }
        if (!this.selectedLocationId) {
          this.selectedLocationId = null;
        }

        this.syncProvinceToSelectedStore();

        document.documentElement.addEventListener('cart:updated', (event) => {
          const count = event?.detail?.cart?.item_count;
          if (typeof count === 'number') {
            this.cartItemCount = count;
            if (window.themeVariables?.settings) {
              window.themeVariables.settings.cartCount = count;
            }
          }
        });

        // Picking a store from somewhere other than the picker — the PDP's list of
        // nearby stores that stock the product. Routed through selectStore so it takes
        // the same path as the picker: province attribute, cart-update form, reload.
        window.addEventListener('age-gate:select-store', async (event) => {
          const storeId = event?.detail?.storeId;
          if (!storeId) return;

          // Awaited: selectStore reads the store list to work out the province that goes
          // on the cart, and an unloaded list would write the location without it.
          await this.ensureStoresLoaded();
          await this.selectStore(String(storeId));
        });

        window.addEventListener('age-gate:open-stores', () => {
          this.pendingStoreId = null;
          this.step = 'stores';
          this.locating = false;
          this.storeSearchQuery = '';
          this.ensureStoresLoaded();
          this.refreshCoordsForDistance();
          this.show();
        });

        window.addEventListener('age-gate:request-select-store', async (event) => {
          const storeId = event?.detail?.storeId;
          if (!storeId) return;
          const returnTo = event?.detail?.returnTo || null;
          await this.ensureStoresLoaded();
          if (returnTo && this.isCurrentStore({ id: storeId })) {
            window.location.href = returnTo;
            return;
          }
          this.requestSelectStore(String(storeId), returnTo);
          if (this.step === 'confirm-store') {
            this.show();
          }
        });

        if (forceShowOnRefresh) {
          this.step = 'gps';
          this.locationError = '';
          this.ageAnswer = null;
          this.ageConfirmed = false;
          this.ageError = false;
          this.rewardsSeen = false;
          this.show();
          return;
        }

        // Ad landing links carry ?age_verified=1 (the ads only reach people of age). The
        // visitor is treated as verified from here on, and this page opens without the
        // gate so Google's crawler and the ad's visitor both see it.
        //
        // A ?province= on the same link becomes the visitor's province, on the cart as
        // well as here, unless they already have a store. The next page then finds age
        // and province answered but no store, and opens the store picker for that
        // province, which cannot be closed until a store is chosen.
        const landingParams = new URLSearchParams(window.location.search);
        const adBypass = landingParams.get('age_verified') === '1';
        if (adBypass) {
          this.setCookie();
          const adProvince = this.normalizeProvince(landingParams.get('province'));
          if (adProvince && !currentLocationId) {
            this.selectedProvince = adProvince;
            if (!isDesignMode) {
              fetch(this.cartUpdateUrl(), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  attributes: { _selected_province: this.toProvinceCode(adProvince) },
                }),
              }).catch(() => {});
            }
          }
        }

        this.ageVerified = this.getCookie();
        if (adBypass) return;

        const hasStore = Boolean(this.selectedLocationId || currentLocationId);
        const requireStore = !this.locationAllowUnselected;

        if (isDesignMode) {
          if (enabledInDesignMode && (!this.selectedProvince || (requireStore && !hasStore))) {
            this.step = this.gpsPermissionChoice ? 'province' : 'gps';
            this.show();
          }
          return;
        }

        // Show on first visit, or when no store has been set (unless unselected is allowed)
        if (!this.ageVerified || !currentProvince || (requireStore && !hasStore)) {
          if (!this.ageVerified || !currentProvince) {
            this.step = this.gpsPermissionChoice ? 'province' : 'gps';
          } else if (this.shouldShowRewards()) {
            this.step = 'rewards';
          } else {
            this.step = 'stores';
            this.ensureStoresLoaded();
            if (this.locationAutoDetect && !hasStore) {
              this.$nextTick(() => this.useLocationOnStoresStep());
            }
          }
          this.show();
        }
      });
    },

    show() {
      this.modalOpen = true;
    },

    hide() {
      this.modalOpen = false;
      this.pendingReturnTo = null;
    },

    get hasSelectedStore() {
      return Boolean(this.selectedLocationId || this.savedCurrentLocationId);
    },

    get canCloseStoresPicker() {
      return this.step === 'stores' && this.hasSelectedStore;
    },

    get currentStore() {
      const id = this.selectedLocationId || this.savedCurrentLocationId;
      if (!id) return null;
      return this.stores.find((store) => String(store.id) === String(id)) || null;
    },

    // The store the nearby filter measures from: whatever is selected now.
    get nearbyOriginStore() {
      const store = this.currentStore;
      if (!store) return null;
      const hasCoords =
        Number.isFinite(Number(store.latitude)) && Number.isFinite(Number(store.longitude));
      return hasCoords || store.city ? store : null;
    },

    get pendingStore() {
      if (!this.pendingStoreId) return null;
      return this.stores.find((store) => String(store.id) === String(this.pendingStoreId)) || null;
    },

    get switchFromStore() {
      if (!this.switchFromStoreId) return this.currentStore;
      return this.stores.find((store) => String(store.id) === String(this.switchFromStoreId)) || null;
    },

    get cartHasItems() {
      const liveCount = window.themeVariables?.settings?.cartCount;
      if (typeof liveCount === 'number') return liveCount > 0;
      return Number(this.cartItemCount) > 0;
    },

    closeModal() {
      if (this.changingStore) return;
      if (this.step === 'confirm-store') {
        this.cancelChangeStore();
        return;
      }
      if (this.step === 'location-warning') {
        this.cancelLocationWarning();
        return;
      }
      if (!this.canCloseStoresPicker) return;
      this.hide();
    },

    requestSelectStore(storeId, returnTo = null) {
      if (!storeId || this.changingStore) return;
      if (this.isCurrentStore({ id: storeId })) return;

      this.pendingReturnTo = returnTo;

      this.skipLocationWarning = false;
      this.pendingUnavailableTitles = [];

      // Confirm whenever switching away from an already-selected store
      if (this.hasSelectedStore) {
        this.switchFromStoreId = this.currentStore ? String(this.currentStore.id) : null;
        this.pendingStoreId = String(storeId);
        this.step = 'confirm-store';
        return;
      }

      // First store pick (or empty cart): still warn if compliance blocks lines
      if (this.cartHasItems) {
        const unavailable = this.getUnavailableTitlesForStore(storeId);
        if (unavailable.length) {
          this.pendingStoreId = String(storeId);
          this.pendingUnavailableTitles = unavailable;
          this.step = 'location-warning';
          return;
        }
      }

      return this.selectStore(storeId);
    },

    cancelChangeStore() {
      if (this.changingStore) return;
      this.pendingStoreId = null;
      this.pendingReturnTo = null;
      this.switchFromStoreId = null;
      this.pendingUnavailableTitles = [];
      this.skipLocationWarning = false;
      this.step = 'stores';
    },

    async confirmChangeStore() {
      if (this.changingStore) return;
      const storeId = this.pendingStoreId;
      if (!storeId) {
        this.step = 'stores';
        return;
      }

      const unavailable = this.getUnavailableTitlesForStore(storeId);
      if (unavailable.length && !this.skipLocationWarning) {
        this.pendingUnavailableTitles = unavailable;
        this.step = 'location-warning';
        return;
      }

      this.changingStore = true;
      try {
        await this.selectStore(storeId);
      } catch (error) {
        this.changingStore = false;
      }
    },

    /**
     * MVP 116: keep cart after warning; proceed with location change.
     */
    async confirmLocationWarningKeep() {
      if (this.changingStore) return;
      const storeId = this.pendingStoreId;
      if (!storeId) {
        this.step = 'stores';
        return;
      }
      this.skipLocationWarning = true;
      this.changingStore = true;
      try {
        await this.selectStore(storeId);
      } catch (error) {
        this.changingStore = false;
        this.skipLocationWarning = false;
      }
    },

    /**
     * MVP 116: return to store picker without clearing cart.
     */
    cancelLocationWarning() {
      if (this.changingStore) return;
      this.pendingUnavailableTitles = [];
      this.skipLocationWarning = false;
      this.step = 'stores';
    },

    // The button sits beside the shopper's store, so it opens that store's page. The old
    // scroll to a Store info section is kept only for a store with no page to link to.
    goToStoreInfo() {
      const url = String(this.currentStore?.url || '').trim();
      if (url) {
        window.location.href = url;
        return;
      }

      this.hide();
      this.$nextTick(() => {
        const target =
          document.querySelector('.shopify-section--store-info') ||
          document.querySelector('[id*="store_info"]');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },

    showProvinceStep() {
      this.step = 'province';
      this.locating = false;
      this.ageError = false;
    },

    showRewardsStep() {
      this.step = 'rewards';
      this.locating = false;
    },

    showStoresStep() {
      // Do not auto-select a store
      this.selectedLocationId = null;
      this.step = 'stores';
      this.locating = false;
      this.ensureStoresLoaded();
      this.refreshCoordsForDistance();
    },

    shouldShowRewards() {
      if (!this.rewardsEnabled) return false;
      // Debug mode: always re-show rewards (ignore persisted choice)
      if (forceShowOnRefresh) return true;
      return !this.rewardsSeen;
    },

    continueAfterProvince() {
      if (this.shouldShowRewards()) {
        this.showRewardsStep();
        return;
      }
      this.showStoresStep();
    },

    markRewardsSeen() {
      // Do not persist when "Always show on refresh" is enabled
      if (forceShowOnRefresh) return;
      this.rewardsSeen = true;
    },

    dismissRewards() {
      this.markRewardsSeen();
      this.showStoresStep();
    },

    joinRewards() {
      this.markRewardsSeen();
      const url = String(this.rewardsPrimaryUrl || '').trim();
      if (url) {
        window.location.href = url;
        return;
      }
      this.showStoresStep();
    },

    async refreshCoordsForDistance() {
      if (this.userCoords) return;
      if (this.gpsPermissionChoice !== 'accepted') return;
      if (isDesignMode || !navigator.geolocation) return;

      try {
        const position = await this.requestPosition();
        this.userCoords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
      } catch (error) {
        // Distance is optional when location is unavailable
      }
    },

    withStoreImage(store) {
      if (!store || typeof store !== 'object') return store;
      if (store.image) return store;
      if (!this.storeImageFallback) return store;
      return { ...store, image: this.storeImageFallback };
    },

    mergeStores(incoming) {
      storeDebug('mergeStores: incoming', incoming);
      if (!Array.isArray(incoming) || incoming.length === 0) {
        storeDebug('mergeStores: nothing to merge (empty or not an array)', incoming);
        return;
      }

      const byId = new Map();
      for (const store of this.stores) {
        byId.set(String(store.id), store);
      }
      for (const store of incoming) {
        if (!store || store.id == null) continue;
        const id = String(store.id);
        const existing = byId.get(id);
        const merged = existing ? { ...existing, ...store } : { ...store };

        // Prefer a real image from either source, then section/theme fallback
        if (!merged.image) {
          merged.image = existing?.image || this.storeImageFallback || '';
        }

        byId.set(id, this.withStoreImage(merged));
      }
      this.stores = Array.from(byId.values());
      storeDebug('mergeStores: total after merge = ' + this.stores.length, this.stores);
      storeDebugBreakdown(this.stores);
    },

    async ensureStoresLoaded() {
      if (this.storesFetched) return this.stores;
      if (!this.locationProductUrl) {
        this.storesFetched = true;
        return this.stores;
      }

      this.storesLoading = true;
      try {
        const url = new URL(this.locationProductUrl, window.location.origin);
        url.searchParams.set('section_id', 'age-gate-store-data');
        if (this.locationVariantId) {
          url.searchParams.set('variant', String(this.locationVariantId));
        }

        storeDebug('fetching age-gate-store-data from ' + url.toString(), null);
        const response = await fetch(url.toString());
        if (!response.ok) {
          storeDebug('fetch failed: HTTP ' + response.status, null);
          return this.stores;
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const node =
          doc.querySelector('[data-age-gate-store-data]') ||
          doc.querySelector('#AgeGateStoreData');
        if (!node) {
          storeDebug('no [data-age-gate-store-data] node in the fetched section', null);
          return this.stores;
        }

        const parsed = JSON.parse(node.textContent.trim() || '[]');
        storeDebug('section returned ' + parsed.length + ' store(s)', parsed);
        this.mergeStores(parsed);
      } catch (error) {
        // Keep any Liquid-rendered / block stores already present
      } finally {
        this.storesFetched = true;
        this.storesLoading = false;
      }

      // Now that the list exists, the saved store's province can be resolved. On init it
      // usually cannot — the stores arrive later — so this is where the picker actually
      // ends up agreeing with the store the shopper is in.
      this.syncProvinceToSelectedStore();

      return this.stores;
    },

    get selectedAge() {
      const name = this.normalizeProvince(this.selectedProvince);
      if (!name) return null;
      return this.ageLimits[name] ?? null;
    },

    get ageQuestionText() {
      if (!this.selectedAge) return '';
      return String(this.ageQuestionTemplate)
        .replace(/__AGE__/g, String(this.selectedAge))
        .replace(/\{\{\s*age\s*\}\}/g, String(this.selectedAge));
    },

    // An age cookie already answers the question: whoever holds one is not asked again.
    // Changing province still clears ageConfirmed, but the cookie stands.
    get ageSatisfied() {
      return this.ageConfirmed || this.ageVerified;
    },

    get canEnterSite() {
      return Boolean(this.selectedProvince) && this.ageSatisfied;
    },

    setAgeAnswer(answer) {
      this.ageAnswer = answer;
      this.ageConfirmed = answer === 'yes';
      if (this.ageConfirmed) {
        this.ageError = false;
      }
    },

    attemptEnterSite() {
      if (!this.selectedProvince) return;
      if (!this.ageSatisfied) {
        this.ageError = true;
        return;
      }
      this.verify();
    },

    get filteredStores() {
      const selected = this.normalizeProvince(this.selectedProvince);
      const query = String(this.storeSearchQuery || '').trim().toLowerCase();

      const rejected = [];
      let list = this.stores
        .filter((store) => {
          if (!selected) return true;
          const storeProvince = this.normalizeProvince(
            store.province || store.provinceCode
          );
          if (!storeProvince || storeProvince !== selected) {
            rejected.push({
              id: store.id,
              store: store.name,
              province: store.province,
              provinceCode: store.provinceCode,
              normalisedTo: storeProvince || '(unresolved)',
            });
            return false;
          }
          return true;
        })
        .map((store) => ({
          ...store,
          distanceKm: this.distanceToStore(store),
        }));

      if (storeDebugEnabled()) {
        storeDebug(
          'filter: selectedProvince=' + JSON.stringify(this.selectedProvince) +
            ' normalised=' + JSON.stringify(selected) +
            ' | ' + list.length + ' of ' + this.stores.length + ' matched',
          null
        );
        if (list.length === 0 && this.stores.length > 0) {
          console.warn('[stores] nothing matched that province. Every store was rejected:');
          console.table(rejected);
          storeDebugBreakdown(this.stores);
        }
      }

      // Every store in the province is listed. Narrowing to the selected store's city
      // or radius left a shopper at an Edmonton store unable to reach any other Alberta
      // store. Without the shopper's own location, the stores nearest their current
      // store sort first instead (sortKm, used only for ordering, never displayed).
      const origin = this.userCoords ? null : this.nearbyOriginStore;
      if (origin) {
        list = list.map((store) => ({ ...store, sortKm: this.kmBetween(origin, store) }));
      }

      if (query) {
        list = list.filter((store) => {
          const haystack = [
            store.name,
            store.address,
            store.address2,
            store.city,
            store.zip,
            store.province,
            store.provinceCode,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(query);
        });
      }

      list.sort((a, b) => {
        const aKm = a.distanceKm ?? a.sortKm;
        const bKm = b.distanceKm ?? b.sortKm;
        if (aKm != null && bKm != null) {
          return aKm - bKm;
        }
        if (aKm != null) return -1;
        if (bKm != null) return 1;
        if (this.suggestedLocationId) {
          if (String(a.id) === String(this.suggestedLocationId)) return -1;
          if (String(b.id) === String(this.suggestedLocationId)) return 1;
        }
        return String(a.name || '').localeCompare(String(b.name || ''));
      });

      return list;
    },

    // Distance between two stores, or null when either lacks usable coordinates.
    kmBetween(a, b) {
      const aLat = Number(a?.latitude);
      const aLng = Number(a?.longitude);
      const bLat = Number(b?.latitude);
      const bLng = Number(b?.longitude);
      if (
        !Number.isFinite(aLat) ||
        !Number.isFinite(aLng) ||
        !Number.isFinite(bLat) ||
        !Number.isFinite(bLng)
      ) {
        return null;
      }
      return this.haversineKm(aLat, aLng, bLat, bLng);
    },

    distanceToStore(store) {
      if (!this.userCoords) return null;
      const lat = Number(store.latitude);
      const lng = Number(store.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return this.haversineKm(
        this.userCoords.lat,
        this.userCoords.lng,
        lat,
        lng
      );
    },

    haversineKm(lat1, lon1, lat2, lon2) {
      const toRad = (deg) => (deg * Math.PI) / 180;
      const r = 6371;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
      return r * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
    },

    formatDistance(store) {
      const km = store.distanceKm != null ? store.distanceKm : this.distanceToStore(store);
      if (km == null || Number.isNaN(km)) return '';
      return `${km.toFixed(1)} km away`;
    },

    formatAddress(store) {
      const line1 = this.formatAddressLine1(store);
      const line2 = this.formatAddressLine2(store);
      return [line1, line2].filter(Boolean).join(' ');
    },

    formatAddressLine1(store) {
      return [store.address, store.address2].filter(Boolean).join(' ');
    },

    formatAddressLine2(store) {
      return [store.city, store.provinceCode || store.province]
        .filter(Boolean)
        .join(', ');
    },

    getTodayHoursKey() {
      const dayNames = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ];
      return `${dayNames[new Date().getDay()]}_hours`;
    },

    normalizeTimeValue(timeValue) {
      if (timeValue == null || timeValue === '') return '';

      if (typeof timeValue === 'object') {
        if (timeValue.value != null && timeValue.value !== '') {
          return this.normalizeTimeValue(timeValue.value);
        }
        if (timeValue.hour != null) {
          const hours = Number(timeValue.hour);
          const minutes = Number(timeValue.minute || 0);
          if (!Number.isNaN(hours)) {
            return `${hours}:${String(minutes).padStart(2, '0')}`;
          }
        }
        return '';
      }

      const time = String(timeValue).trim();
      if (!time || time === '[object Object]' || /^until$/i.test(time)) return '';

      const isoMatch = time.match(/T(\d{1,2}:\d{2}(?::\d{2})?)/);
      if (isoMatch) return isoMatch[1].slice(0, 5);

      const rangeMatch = time.match(
        /(?:\d{1,2}(?::\d{2})?\s*(?:am|pm)?)\s*[-–—]\s*(\d{1,2}(?::\d{2})?\s*(?:am|pm)?)/i
      );
      if (rangeMatch) return rangeMatch[1].trim();

      const ampmLoose = time.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i);
      if (ampmLoose) return time;

      return time.split('.')[0].slice(0, 5);
    },

    resolveStoreCloseTime(store) {
      if (!store) return '';

      if (store.storeCloseTime) {
        const resolved = this.normalizeTimeValue(store.storeCloseTime);
        if (resolved) return resolved;
      }

      const dayKey = this.getTodayHoursKey();
      if (store.hoursByWeekday && store.hoursByWeekday[dayKey]) {
        const weekdayTime = String(store.hoursByWeekday[dayKey]).trim();
        if (/am|pm/i.test(weekdayTime)) return weekdayTime;
        const resolved = this.normalizeTimeValue(weekdayTime);
        if (resolved) return resolved;
      }

      const hoursSource = store.storeHours || store.storeHoursPlain;
      if (!hoursSource) return '';

      if (typeof hoursSource === 'object') {
        return this.normalizeTimeValue(hoursSource);
      }

      const time = String(hoursSource).trim();
      if (!time || time === '[object Object]') return '';

      if (/^until\b/i.test(time)) {
        return time.replace(/^until\s*/i, '').trim();
      }

      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const todayAbbr = dayNames[new Date().getDay()];
      const lineMatch = time.match(
        new RegExp(`(?:^|\\n)\\s*${todayAbbr}[a-z]*\\s+(.+)$`, 'im')
      );
      if (lineMatch) {
        return this.normalizeTimeValue(lineMatch[1].trim());
      }

      const rangeMatch = time.match(
        /(?:\d{1,2}(?::\d{2})?\s*(?:am|pm)?)\s*[-–—]\s*(\d{1,2}(?::\d{2})?\s*(?:am|pm)?)/i
      );
      if (rangeMatch) return rangeMatch[1].trim();

      return this.normalizeTimeValue(time);
    },

    formatStoreHours(store) {
      if (!store) return '';

      if (store.storeUntil) {
        const until = String(store.storeUntil).trim();
        if (until && !/^until$/i.test(until)) return until;
      }

      const closeTime = this.resolveStoreCloseTime(store);
      if (!closeTime) return '';

      if (/^until\b/i.test(closeTime)) {
        const untilTime = closeTime.replace(/^until\s*/i, '').trim();
        return untilTime ? closeTime : '';
      }

      if (/^closed\b/i.test(closeTime)) return closeTime;
      if (/am|pm/i.test(closeTime)) return `Until ${closeTime}`;

      const formatted = this.format24HourTo12Hour(closeTime);
      return formatted ? `Until ${formatted}` : '';
    },

    formatCloseTimeLabel(store) {
      const hours = this.formatStoreHours(store);
      if (!hours) return '';
      return hours.replace(/^until\s*/i, '').trim();
    },

    formatMyStoreStatus(store) {
      if (!store) return '';
      const isOpen = store.available !== false;
      const closeLabel = this.formatCloseTimeLabel(store);
      if (!isOpen) {
        return closeLabel ? `Closed · Opens ${closeLabel}` : 'Closed';
      }
      return closeLabel ? `Open · Closes ${closeLabel}` : 'Open';
    },

    formatAvailabilitySummary(store) {
      if (!store) return '';
      const services = [];
      if (this.hasPickup(store)) services.push('Pickup');
      if (this.hasDelivery(store)) services.push('Delivery');
      if (!services.length) return '';

      const serviceText =
        services.length === 2 ? 'Pickup and Delivery' : services[0];
      const closeLabel = this.formatCloseTimeLabel(store);
      const openLabel = this.formatOpenTimeLabel(store);

      if (openLabel && closeLabel) {
        return `✓ ${serviceText} available Monday-Sunday: ${openLabel} - ${closeLabel}`;
      }
      if (closeLabel) {
        return `✓ ${serviceText} available Monday-Sunday: Closes ${closeLabel}`;
      }
      return `✓ ${serviceText} available Monday-Sunday`;
    },

    formatOpenTimeLabel(store) {
      if (!store) return '';

      if (store.storeOpenTime) {
        const resolved = this.normalizeTimeValue(store.storeOpenTime);
        if (resolved) {
          if (/am|pm/i.test(resolved)) return resolved;
          return this.format24HourTo12Hour(resolved) || resolved;
        }
      }

      const dayKey = this.getTodayHoursKey();
      const weekdayHours =
        store.hoursByWeekday && store.hoursByWeekday[dayKey]
          ? String(store.hoursByWeekday[dayKey]).trim()
          : '';

      if (weekdayHours) {
        const rangeMatch = weekdayHours.match(/^(\d{1,2}:\d{2}(?:\s*[ap]m)?)/i);
        if (rangeMatch) {
          const open = rangeMatch[1].trim();
          if (/am|pm/i.test(open)) return open;
          return this.format24HourTo12Hour(open) || open;
        }
      }

      return '';
    },

    format24HourTo12Hour(time24) {
      const parts = String(time24).trim().split(':');
      let hours = parseInt(parts[0], 10);
      const minutes = parts[1] != null ? parseInt(parts[1], 10) : 0;
      if (Number.isNaN(hours)) return '';
      const period = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      if (minutes) {
        return `${hours}:${String(minutes).padStart(2, '0')} ${period}`;
      }
      return `${hours} ${period}`;
    },

    formatPhone(store) {
      if (!store.phone) return '';
      return String(store.phone).trim();
    },

    hasPickup(store) {
      return Boolean(store.pickup);
    },

    hasDelivery(store) {
      return Boolean(store.delivery);
    },

    isCurrentStore(store) {
      if (!store || store.id == null) return false;
      const id = String(store.id);
      if (this.selectedLocationId && String(this.selectedLocationId) === id) {
        return true;
      }
      if (this.savedCurrentLocationId && this.savedCurrentLocationId === id) {
        return true;
      }
      return false;
    },

    onProvinceChange() {
      this.ageError = false;
      this.ageAnswer = null;
      this.ageConfirmed = false;
    },

    normalizeProvince(value) {
      if (!value) return null;
      const key = String(value).trim();
      if (PROVINCE_ALIASES[key]) return PROVINCE_ALIASES[key];

      const lower = key.toLowerCase();
      for (const [alias, province] of Object.entries(PROVINCE_ALIASES)) {
        if (alias.toLowerCase() === lower) return province;
      }
      return null;
    },

    toProvinceCode(value) {
      if (!value) return null;
      const key = String(value).trim();
      if (PROVINCE_NAME_TO_CODE[key]) return PROVINCE_NAME_TO_CODE[key];
      const name = this.normalizeProvince(key);
      if (name && PROVINCE_NAME_TO_CODE[name]) return PROVINCE_NAME_TO_CODE[name];
      if (/^[A-Za-z]{2}$/.test(key)) return key.toUpperCase();
      return null;
    },

    // The province follows the chosen store, always. It is persisted separately, so the
    // two could disagree: a shopper who switched store from the PDP or the store locator
    // kept whatever province they had picked earlier, and reopening the picker showed
    // that stale province with its stores rather than the one they are actually shopping.
    //
    // The store is the stronger signal — it is what the cart is keyed on — so where they
    // disagree the store wins. Called on init and whenever the store list arrives, since
    // the province name can only be resolved once the store is in the list.
    syncProvinceToSelectedStore() {
      const storeId = this.selectedLocationId || this.savedCurrentLocationId;
      if (!storeId || !Array.isArray(this.stores) || this.stores.length === 0) return;

      const store = this.stores.find((entry) => String(entry.id) === String(storeId));
      if (!store) return;

      const province = this.normalizeProvince(store.province || store.provinceCode);
      if (province && province !== this.selectedProvince) {
        this.selectedProvince = province;
      }
    },

    provinceCodeForCart(storeId) {
      const store = (this.stores || []).find((s) => String(s.id) === String(storeId));
      return (
        this.toProvinceCode(store?.provinceCode) ||
        this.toProvinceCode(store?.province) ||
        this.toProvinceCode(this.selectedProvince)
      );
    },

    readCartComplianceMap() {
      const el = document.getElementById('AgeGate-cart-compliance');
      if (!el) return {};
      try {
        return JSON.parse(el.textContent || '{}') || {};
      } catch (e) {
        return {};
      }
    },

    readCartLineTitles() {
      const el = document.getElementById('AgeGate-cart-lines');
      if (!el) return [];
      try {
        const lines = JSON.parse(el.textContent || '[]');
        return Array.isArray(lines) ? lines : [];
      } catch (e) {
        return [];
      }
    },

    isProductAllowedInProvince(productId, provinceCode) {
      if (!provinceCode) return true;
      const map = this.readCartComplianceMap();
      const intended = map[String(productId)];

      // Soft fail: missing metafield = allowed (MVP 5.6)
      if (intended == null || intended === '') return true;

    // `compliance.intended_province` reaches the page in two shapes: a list metafield
    // becomes a real array, and a text metafield holding JSON becomes the STRING
    // `["AB", "ON"]`. Splitting that string on commas yields `["AB"` and `"ON"]`, which
    // match no province, so every product carrying the JSON form read as unavailable
    // everywhere. Parse it first, and strip the punctuation off whatever survives.
      let list = intended;
      if (typeof list === 'string') {
        try {
          const parsed = JSON.parse(list);
          if (Array.isArray(parsed)) list = parsed;
        } catch (e) {
          // Not JSON: a plain comma-separated string, handled below.
        }
      }
      if (!Array.isArray(list)) list = String(list).split(',');
      list = list
        .map((part) => String(part).replace(/[^A-Za-z]/g, '').toUpperCase())
        .filter(Boolean);
      if (!list.length) return true;

      const code = String(provinceCode).trim().toUpperCase();
      return list.indexOf(code) !== -1;
    },

    getUnavailableTitlesForStore(storeId) {
      const provinceCode = this.provinceCodeForCart(storeId);
      if (!provinceCode) return [];

      const titles = [];
      const seen = {};
      for (const line of this.readCartLineTitles()) {
        if (!line || line.product_id == null) continue;
        if (this.isProductAllowedInProvince(line.product_id, provinceCode)) continue;
        const title = String(line.title || '').trim() || `Product ${line.product_id}`;
        if (seen[title]) continue;
        seen[title] = true;
        titles.push(title);
      }
      return titles;
    },

    provinceFromCoordinates(lat, lng) {
      const match = CANADIAN_PROVINCE_BOUNDS.find(
        (province) =>
          lat >= province.minLat &&
          lat <= province.maxLat &&
          lng >= province.minLng &&
          lng <= province.maxLng
      );
      return match ? match.name : null;
    },

    getCurrentPosition(options) {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, options);
      });
    },

    async requestPosition() {
      // Network/IP-based first (more reliable on desktop), then high-accuracy GPS.
      const attempts = [
        { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 },
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
      ];

      let lastError = null;
      for (const options of attempts) {
        try {
          return await this.getCurrentPosition(options);
        } catch (error) {
          lastError = error;
        }
      }
      throw lastError || new Error('Unable to get position');
    },

    async reverseGeocodeProvince(lat, lng) {
      // Best-effort only. Browser CORS / rate limits often block this.
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 4000);
        const url = `https://geocode.maps.co/reverse?lat=${encodeURIComponent(
          lat
        )}&lon=${encodeURIComponent(lng)}`;
        const response = await fetch(url, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        clearTimeout(timer);
        if (!response.ok) return null;

        const data = await response.json();
        const address = data.address || {};
        return (
          this.normalizeProvince(address.state) ||
          this.normalizeProvince(address.province) ||
          this.normalizeProvince(address.region) ||
          null
        );
      } catch (error) {
        return null;
      }
    },

    async detectProvinceFromPosition(position) {
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      this.userCoords = { lat, lng };

      // Prefer local bounds so detection works even when reverse-geocode is blocked.
      const fromBounds = this.provinceFromCoordinates(lat, lng);
      if (fromBounds) return fromBounds;

      return this.reverseGeocodeProvince(lat, lng);
    },

    denyLocation() {
      // Remember choice so GPS popup only appears on first visit
      this.gpsPermissionChoice = 'denied';
      this.locationError = '';
      this.showProvinceStep();
    },

    nearestStoreIdForProvince(province) {
      if (!province || !this.stores.length) return null;

      const selected = this.normalizeProvince(province);
      const provincial = this.stores.filter((store) => {
        const storeProvince = this.normalizeProvince(store.province || store.provinceCode);
        return !storeProvince || storeProvince === selected;
      });
      if (!provincial.length) return null;

      if (this.userCoords) {
        let best = null;
        let bestDistance = Infinity;
        for (const store of provincial) {
          const distance = this.distanceToStore(store);
          if (distance != null && distance < bestDistance) {
            bestDistance = distance;
            best = store;
          }
        }
        if (best) return best.id;
      }

      return provincial[0].id;
    },

    /**
     * Accept GPS — remember choice, auto-detect province/nearest store context,
     * then continue into age/province confirmation (or stores if already verified).
     */
    async allowLocation() {
      this.locationError = '';

      if (isDesignMode) {
        this.gpsPermissionChoice = 'accepted';
        this.locationError = 'location_editor';
        this.showProvinceStep();
        return;
      }

      if (!navigator.geolocation) {
        this.gpsPermissionChoice = 'denied';
        this.locationError = 'unsupported';
        this.showProvinceStep();
        return;
      }

      this.locating = true;
      this.gpsPermissionChoice = 'accepted';

      try {
        await this.ensureStoresLoaded();

        const position = await this.requestPosition();
        const province = await this.detectProvinceFromPosition(position);

        if (province) {
          this.selectedProvince = province;
          this.locationError = '';
          // Prefill nearest store suggestion (user still confirms age, then picks store)
          this.suggestedLocationId = this.nearestStoreIdForProvince(province);
        } else {
          this.locationError = 'detect_failed';
        }

        // Still need province + age confirmation before store selection
        this.showProvinceStep();
      } catch (error) {
        // Permission may still be allowed; position can time out or be unavailable.
        this.locationError = 'detect_failed';
        this.showProvinceStep();
      } finally {
        this.locating = false;
      }
    },

    async setCookie() {
      let cookie = `${this.cookieName}=${this.cookieValue};path=/`;

      if (expirationDuration > 0) {
        const date = new Date();
        date.setTime(date.getTime() + expirationDuration * 24 * 60 * 60 * 1000);
        cookie += `;expires=${date.toUTCString()}`;
      }

      document.cookie = cookie;
      this.ageVerified = true;
    },

    getCookie() {
      const name = this.cookieName + '=';
      const parts = decodeURIComponent(document.cookie).split(';');

      for (let part of parts) {
        while (part.charAt(0) === ' ') {
          part = part.substring(1);
        }
        if (part.indexOf(name) === 0) {
          return true;
        }
      }

      return false;
    },

    cartUpdateUrl() {
      const root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
        (window.themeVariables &&
        window.themeVariables.routes &&
        window.themeVariables.routes.rootUrlWithoutSlash
          ? `${window.themeVariables.routes.rootUrlWithoutSlash}/`
          : '/');

      return `${root}cart/update.js`;
    },

    async updateCartAttributes({ reload = false } = {}) {
      const locationId = this.selectedLocationId ? String(this.selectedLocationId) : '';
      const provinceCode =
        this.toProvinceCode(this.selectedProvince) || String(this.selectedProvince || '').trim();

      const attributes = {
        _selected_province: provinceCode,
        _selected_location_id: locationId,
        _selected_location: locationId ? `gid://shopify/Location/${locationId}` : '',
      };

      await fetch(this.cartUpdateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      });

      if (reload) {
        window.location.href = window.location.href.split('?')[0];
      }
    },

    /**
     * Select Province Location — Enter Site.
     * Requires province + age confirmation; then Rise Rewards, then Nearby Stores.
     */
    async verify() {
      if (!this.selectedProvince) return;

      if (!this.ageSatisfied) {
        this.ageError = true;
        return;
      }

      this.ageError = false;
      this.selectedLocationId = null;

      if (!this.ageVerified) {
        await this.setCookie();
      }

      if (!isDesignMode) {
        // MVP keep-cart: update province + clear location attrs only (do not empty cart)
        const provinceCode =
          this.toProvinceCode(this.selectedProvince) || String(this.selectedProvince || '').trim();
        try {
          await fetch(this.cartUpdateUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              attributes: {
                _selected_province: provinceCode,
                _selected_location_id: '',
                _selected_location: '',
              },
            }),
          });
        } catch (e) {
          await this.updateCartAttributes({ reload: false });
        }
      }

      this.continueAfterProvince();
    },

    async selectStore(storeId) {
      if (!storeId) return;

      // Keep pendingStoreId while confirming so the modal copy stays visible during loading
      if (!this.changingStore) {
        this.pendingStoreId = null;
      }
      this.selectedLocationId = String(storeId);
      this.savedCurrentLocationId = String(storeId);

      const provinceCode = this.provinceCodeForCart(storeId);
      if (provinceCode) {
        // Keep UI province in sync with the store we are selecting
        const provinceName = this.normalizeProvince(provinceCode);
        if (provinceName) this.selectedProvince = provinceName;
      }

      document.dispatchEvent(
        new CustomEvent('age-gate:store-selected', {
          detail: { storeId: String(storeId), province: provinceCode },
        })
      );

      if (!isDesignMode) {
        // Prefer cart/update form with optional line-item remap + filtered return_to
        const form = document.getElementById(`location-cart-update-${storeId}`);
        if (form) {
          if (provinceCode) {
            let provinceInput = form.querySelector('input[name="attributes[_selected_province]"]');
            if (!provinceInput) {
              provinceInput = document.createElement('input');
              provinceInput.type = 'hidden';
              provinceInput.name = 'attributes[_selected_province]';
              form.appendChild(provinceInput);
            }
            provinceInput.value = provinceCode;
          }

          let gidInput = form.querySelector('input[name="attributes[_selected_location]"]');
          if (!gidInput) {
            gidInput = document.createElement('input');
            gidInput.type = 'hidden';
            gidInput.name = 'attributes[_selected_location]';
            form.appendChild(gidInput);
          }
          gidInput.value = `gid://shopify/Location/${storeId}`;

          if (this.pendingReturnTo) {
            let returnInput = form.querySelector('input[name="return_to"]');
            if (!returnInput) {
              returnInput = document.createElement('input');
              returnInput.type = 'hidden';
              returnInput.name = 'return_to';
              form.appendChild(returnInput);
            }
            returnInput.value = this.pendingReturnTo;
          }

          form.submit();
          return;
        }

        if (this.pendingReturnTo) {
          const returnTo = this.pendingReturnTo;
          await this.updateCartAttributes();
          window.location.href = returnTo;
          return;
        }

        await this.updateCartAttributes({ reload: true });
        return;
      }

      this.changingStore = false;
      this.pendingStoreId = null;
      this.skipLocationWarning = false;
      this.pendingUnavailableTitles = [];
      this.hide();
    },

    async clearLocationAndCart() {
      // Name retained for callers; MVP keep-cart — clear location attrs only.
      this.selectedLocationId = null;
      if (!isDesignMode) {
        const provinceCode =
          this.toProvinceCode(this.selectedProvince) || String(this.selectedProvince || '').trim();
        await fetch(this.cartUpdateUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              _selected_province: provinceCode,
              _selected_location_id: '',
              _selected_location: '',
            },
          }),
        });
        window.location.href = window.location.href.split('?')[0];
        return;
      }
      await this.updateCartAttributes({ reload: true });
    },

    async useLocationOnStoresStep() {
      this.locationError = '';

      if (isDesignMode) {
        this.locationError = 'location_editor';
        return;
      }

      if (!navigator.geolocation) {
        this.locationError = 'unsupported';
        return;
      }

      this.locating = true;

      try {
        await this.ensureStoresLoaded();

        const position = await this.requestPosition();
        const province = await this.detectProvinceFromPosition(position);
        if (province) {
          this.selectedProvince = province;
          this.suggestedLocationId = this.nearestStoreIdForProvince(province);
          this.locationError = '';
        } else if (this.userCoords && this.stores.length > 0) {
          // Province unknown, but we can still sort by distance
          this.suggestedLocationId = this.nearestStoreIdForCoords();
          this.locationError = '';
        } else {
          this.locationError = 'detect_failed';
        }
      } catch (error) {
        this.locationError = 'detect_failed';
      } finally {
        this.locating = false;
      }
    },

    nearestStoreIdForCoords() {
      if (!this.userCoords || !this.stores.length) return null;

      let best = null;
      let bestDistance = Infinity;
      for (const store of this.stores) {
        const distance = this.distanceToStore(store);
        if (distance != null && distance < bestDistance) {
          bestDistance = distance;
          best = store;
        }
      }
      return best ? best.id : null;
    },

  };
}

document.addEventListener('alpine:init', () => {
  Alpine.data('provinceChanger', provinceChanger);
});
